import { databases, DATABASE_ID, COLLECTIONS, ID, Query } from "./appwrite";

export interface QuestionAttempt {
  $id?: string;
  userId: string;
  subjectId: string;
  moduleId: string;
  topicId: string;
  questionId: string;
  selectedAnswer: string | null;
  isCorrect: boolean;
  isSkipped: boolean;
  timeSpent: number;
  attemptNumber: number;
  sessionId?: string;
  createdAt: string;
}

export interface UserStats {
  $id?: string;
  userId: string;
  totalAttempted: number;
  totalCorrect: number;
  totalSkipped: number;
  accuracy: number;
  totalTimeSpent: number;
  statsBySubject: Record<
    string,
    { attempted: number; correct: number; accuracy: number }
  >;
  statsByTopic: Record<
    string,
    { attempted: number; correct: number; accuracy: number }
  >;
  weakTopics: string[];
  lastSubjectId: string | null;
  lastModuleId: string | null;
  lastTopicId: string | null;
  lastActivityAt: string | null;
  updatedAt: string;
}

/**
 * Submit a question attempt
 */
export async function submitAttempt(
  attempt: Omit<QuestionAttempt, "$id" | "createdAt">
): Promise<QuestionAttempt> {
  try {
    const doc = await databases.createDocument(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      ID.unique(),
      {
        ...attempt,
        createdAt: new Date().toISOString(),
      }
    );
    return doc as unknown as QuestionAttempt;
  } catch (error: any) {
    console.error("Error submitting attempt:", error);
    if (error.code === 404) {
      console.warn(
        "⚠️ Question attempts collection not found. Please create it in Appwrite Console."
      );
    }
    throw error;
  }
}

/**
 * Get attempt count for a specific question
 */
export async function getAttemptCount(
  userId: string,
  questionId: string
): Promise<number> {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUESTION_ATTEMPTS,
    [Query.equal("userId", userId), Query.equal("questionId", questionId)]
  );
  return response.total;
}

/**
 * Record a single answered question and update the user_stats document atomically.
 * Creates the stats doc if it doesn't exist yet.
 */
export async function recordAnswer(params: {
  userId: string;
  subjectId: string;
  moduleId: string;
  topicId: string;
  questionId: string;
  selectedAnswer: string;
  isCorrect: boolean;
  timeSpent: number;
  sessionId: string;
}, knownStats?: UserStats | null): Promise<UserStats> {
  const { userId, subjectId, moduleId, topicId, questionId, selectedAnswer, isCorrect, timeSpent, sessionId } = params;

  // 1. Save attempt record (fire-and-forget, non-fatal if collection missing)
  try {
    await databases.createDocument(DATABASE_ID, COLLECTIONS.QUESTION_ATTEMPTS, ID.unique(), {
      userId, subjectId, moduleId, topicId, questionId,
      selectedAnswer, isCorrect, isSkipped: false, timeSpent,
      attemptNumber: 1, sessionId,
      createdAt: new Date().toISOString(),
    });
  } catch (e: any) {
    console.warn("Could not save attempt:", e.message);
  }

  // 2. Upsert user_stats document
  const now = new Date().toISOString();
  let existing: UserStats | null = knownStats ?? null;
  if (!existing) {
    try {
      const doc = await databases.getDocument(DATABASE_ID, COLLECTIONS.USER_STATS, userId);
      existing = {
        ...doc,
        statsBySubject: JSON.parse((doc.statsBySubject as string) || "{}"),
        statsByTopic: JSON.parse((doc.statsByTopic as string) || "{}"),
        weakTopics: JSON.parse((doc.weakTopics as string) || "[]"),
      } as unknown as UserStats;
    } catch { /* doc doesn't exist yet */ }
  }

  const base: UserStats = existing ?? {
    userId,
    totalAttempted: 0, totalCorrect: 0, totalSkipped: 0,
    accuracy: 0, totalTimeSpent: 0,
    statsBySubject: {}, statsByTopic: {},
    weakTopics: [],
    lastSubjectId: null, lastModuleId: null, lastTopicId: null,
    lastActivityAt: null, updatedAt: now,
  };

  // Update totals
  base.totalAttempted += 1;
  if (isCorrect) base.totalCorrect += 1;
  base.totalTimeSpent += timeSpent;
  base.accuracy = base.totalAttempted > 0 ? (base.totalCorrect / base.totalAttempted) * 100 : 0;
  base.lastSubjectId = subjectId;
  base.lastModuleId = moduleId;
  base.lastTopicId = topicId;
  base.lastActivityAt = now;
  base.updatedAt = now;

  // Per-subject stats
  const subj = base.statsBySubject[subjectId] ?? { attempted: 0, correct: 0, accuracy: 0 };
  subj.attempted += 1;
  if (isCorrect) subj.correct += 1;
  subj.accuracy = (subj.correct / subj.attempted) * 100;
  base.statsBySubject[subjectId] = subj;

  // Per-topic stats
  const topic = base.statsByTopic[topicId] ?? { attempted: 0, correct: 0, accuracy: 0 };
  topic.attempted += 1;
  if (isCorrect) topic.correct += 1;
  topic.accuracy = (topic.correct / topic.attempted) * 100;
  base.statsByTopic[topicId] = topic;

  // Weak topics = accuracy < 60% with ≥3 attempts
  base.weakTopics = Object.entries(base.statsByTopic)
    .filter(([, v]) => v.attempted >= 3 && v.accuracy < 60)
    .sort((a, b) => a[1].accuracy - b[1].accuracy)
    .map(([k]) => k);

  const payload = {
    userId: base.userId,
    totalAttempted: base.totalAttempted,
    totalCorrect: base.totalCorrect,
    totalSkipped: base.totalSkipped,
    accuracy: base.accuracy,
    totalTimeSpent: base.totalTimeSpent,
    statsBySubject: JSON.stringify(base.statsBySubject),
    statsByTopic: JSON.stringify(base.statsByTopic),
    weakTopics: JSON.stringify(base.weakTopics),
    lastSubjectId: base.lastSubjectId,
    lastModuleId: base.lastModuleId,
    lastTopicId: base.lastTopicId,
    lastActivityAt: base.lastActivityAt,
    updatedAt: base.updatedAt,
  };

  try {
    if (existing) {
      await databases.updateDocument(DATABASE_ID, COLLECTIONS.USER_STATS, userId, payload);
    } else {
      await databases.createDocument(DATABASE_ID, COLLECTIONS.USER_STATS, userId, payload);
    }
  } catch (e: any) {
    console.warn("Could not update user stats:", e.message);
  }

  return base;
}

/**
 * Get user's stats for dashboard
 */
export async function getUserStats(userId: string): Promise<UserStats | null> {
  try {
    const doc = await databases.getDocument(
      DATABASE_ID,
      COLLECTIONS.USER_STATS,
      userId // Using userId as document ID
    );

    // Parse JSON strings back to objects
    return {
      ...doc,
      statsBySubject: JSON.parse((doc.statsBySubject as string) || "{}"),
      statsByTopic: JSON.parse((doc.statsByTopic as string) || "{}"),
      weakTopics: JSON.parse((doc.weakTopics as string) || "[]"),
    } as unknown as UserStats;
  } catch {
    return null;
  }
}

/**
 * Get recent attempts for a user
 */
export async function getRecentAttempts(
  userId: string,
  limit: number = 10
): Promise<QuestionAttempt[]> {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUESTION_ATTEMPTS,
    [
      Query.equal("userId", userId),
      Query.orderDesc("createdAt"),
      Query.limit(limit),
    ]
  );
  return response.documents as unknown as QuestionAttempt[];
}

/**
 * Get progress for a specific topic
 */
export async function getTopicProgress(
  userId: string,
  topicId: string
): Promise<{ attempted: number; correct: number; total: number }> {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUESTION_ATTEMPTS,
    [Query.equal("userId", userId), Query.equal("topicId", topicId)]
  );

  const attempts = response.documents as unknown as QuestionAttempt[];
  const uniqueQuestions = new Set(attempts.map((a) => a.questionId));
  const correctAttempts = attempts.filter((a) => a.isCorrect);

  return {
    attempted: uniqueQuestions.size,
    correct: correctAttempts.length,
    total: 0, // Would need to be passed in or fetched
  };
}

/**
 * Generate a unique session ID for a practice session
 */
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Count the total number of distinct practice sessions a user has started
 * (only sessions where at least one question was answered are counted).
 */
export async function countUserPracticeSessions(userId: string): Promise<number> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [Query.equal("userId", userId), Query.limit(500)]
    );
    const sessions = new Set<string>();
    for (const doc of response.documents as unknown as QuestionAttempt[]) {
      if (doc.sessionId) sessions.add(doc.sessionId);
    }
    return sessions.size;
  } catch {
    return 0;
  }
}

export interface SessionInfo {
  sessionId: string;
  createdAt: string;
  questionCount: number;
  correctCount: number;
}

export async function getSessionsForTopic(
  userId: string,
  topicId: string
): Promise<SessionInfo[]> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [
        Query.equal("userId", userId),
        Query.equal("topicId", topicId),
        Query.orderDesc("createdAt"),
        Query.limit(500),
      ]
    );

    const sessions = new Map<string, SessionInfo>();
    for (const doc of response.documents as unknown as QuestionAttempt[]) {
      const sid = doc.sessionId ?? "unknown";
      const existing = sessions.get(sid);
      if (!existing) {
        sessions.set(sid, {
          sessionId: sid,
          createdAt: doc.createdAt,
          questionCount: 1,
          correctCount: doc.isCorrect ? 1 : 0,
        });
      } else {
        existing.questionCount += 1;
        if (doc.isCorrect) existing.correctCount += 1;
        if (doc.createdAt < existing.createdAt) existing.createdAt = doc.createdAt;
      }
    }

    return Array.from(sessions.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch {
    return [];
  }
}

export async function getAttemptsForSession(
  userId: string,
  topicId: string,
  sessionId: string
): Promise<QuestionAttempt[]> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [
        Query.equal("userId", userId),
        Query.equal("topicId", topicId),
        Query.equal("sessionId", sessionId),
        Query.orderAsc("createdAt"),
        Query.limit(500),
      ]
    );
    return response.documents as unknown as QuestionAttempt[];
  } catch {
    return [];
  }
}

export async function getAllTopicAttempts(
  userId: string,
  topicId: string
): Promise<QuestionAttempt[]> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [
        Query.equal("userId", userId),
        Query.equal("topicId", topicId),
        Query.orderDesc("createdAt"),
        Query.limit(500),
      ]
    );

    // Keep only the most recent attempt per question
    const seen = new Set<string>();
    const latest: QuestionAttempt[] = [];
    for (const doc of response.documents as unknown as QuestionAttempt[]) {
      const key = String(doc.questionId);
      if (!seen.has(key)) {
        seen.add(key);
        latest.push(doc);
      }
    }
    return latest;
  } catch {
    return [];
  }
}

export interface QuestionAttemptStatus {
  lastCorrect: boolean;
  attemptCount: number;
}

/**
 * Fetch the most recent attempt status for each question a user has tried in a topic.
 * Returns a map of questionId (as string) → status.
 */
export async function getTopicAttemptStatus(
  userId: string,
  topicId: string
): Promise<Map<string, QuestionAttemptStatus>> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [
        Query.equal("userId", userId),
        Query.equal("topicId", topicId),
        Query.orderDesc("createdAt"),
        Query.limit(500),
      ]
    );

    const statusMap = new Map<string, QuestionAttemptStatus>();
    for (const doc of response.documents as unknown as QuestionAttempt[]) {
      const key = String(doc.questionId);
      if (!statusMap.has(key)) {
        // First entry is the most recent (orderDesc)
        statusMap.set(key, { lastCorrect: doc.isCorrect, attemptCount: 1 });
      } else {
        statusMap.get(key)!.attemptCount += 1;
      }
    }
    return statusMap;
  } catch {
    return new Map();
  }
}

/**
 * Fetch all attempts for a specific subject for a user (up to 500).
 * Used to derive detailed per-subject analytics.
 */
export async function getSubjectAttempts(
  userId: string,
  subjectId: string
): Promise<QuestionAttempt[]> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUESTION_ATTEMPTS,
      [
        Query.equal("userId", userId),
        Query.equal("subjectId", subjectId),
        Query.orderAsc("createdAt"),
        Query.limit(500),
      ]
    );
    return response.documents as unknown as QuestionAttempt[];
  } catch {
    return [];
  }
}
